import { useHistory } from 'umi';
import { LanguageType } from '../types/common';
import { getBasePath } from '@/utils/utils';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import moment from 'moment';
import { getFeedsStats } from '@op/services';
import { JumpType, navigate } from '@home/utils/utils';

/**
 * 跳转外部链接
 * @param url 链接
 */
export function openWindowUrl(url?: string, data?: any) {
  url && window.open(url, data);
}

function analysisUrl(
  url: string,
  forEachFunc: (key: string, value: string) => void,
) {
  const urlParam = url?.substring(url.indexOf('?') + 1, url.length);
  const urlParamList = urlParam.split('&');
  urlParamList.forEach((item) => {
    if (item.includes('=')) {
      const [key, ...otherInfo] = item.split('=');
      forEachFunc(key, otherInfo.join('='));
    }
  });
}

export function urlToMap(url: string): Map<string, string> {
  const map = new Map();
  analysisUrl(url, (key, value) => {
    map.set(key, value);
  });
  return map;
}

export function urlToObj(url: string): { [key: string]: string } {
  const map: { [key: string]: string } = {};
  analysisUrl(url, (key, value) => {
    map[key] = value;
  });
  return map;
}

/**
 * TODO: 其他系统使用
 * 将对象转为链接形式
 * @param query
 * @param keyMap
 * @returns
 */
export function objToUrl(
  query: { [key: string]: Array<any> | string },
  keyMap: { [key: string]: string } = {},
) {
  const url: Array<string> = [];
  Object.keys(query || {}).forEach((key) => {
    const element = query[key];
    const newKey = keyMap[key] || key;
    url.push(
      `${newKey}=${Array.isArray(element) ? element.join(',') : element}`,
    );
  });
  return url.join('&');
}

export function useInnerSystemRouter({
  gameInfo,
  commonUrl,
}: {
  gameInfo: any;
  commonUrl: string;
}) {
  const { push } = useHistory();
  const toOtherPage = (params: any, url: string = commonUrl) => {
    const newUrl = url + url.startsWith('?') ? '' : '?';
    push(newUrl + objToUrl({ ...gameInfo, noCache: true, ...params }));
  };
  return { toOtherPage };
}

const channelUrlMap: any = {
  facebook: 'https://www.facebook.com/',
  twitter: 'https://twitter.com/',
  instagram: 'https://www.instagram.com/',
  reddit: 'https://www.reddit.com/',
  tiktok: 'https://www.tiktok.com/',
  youtube: 'https://www.youtube.com/',
};
export function goToChannelUrl(channel: string) {
  if (channel) {
    const key = channel.toLocaleLowerCase();
    openWindowUrl(channelUrlMap[key]);
  }
}

export function getUrlGameId(url?: string) {
  url = url || location.href;
  const query = urlToMap(location.href);
  const urlGameId = query.get('gameid');

  return urlGameId;
}

export const getGameIdIsSame = (gameInfo: { edition_unified_id: string }) => {
  const gameId = gameInfo?.edition_unified_id;
  const urlGameId = getUrlGameId() || gameId;

  return { isSameId: gameId && gameId === urlGameId, gameId, urlGameId };
};

export const goToFeedsReviewsDetail = (
  history: any,
  data: { comment_uin: string; comment_time: string },
  urlParams: {
    language: LanguageType;
    tab?: 'socialMedia' | 'gameStore';
    gameid?: string;
  } = { language: 'origin', },
  jumpType?: JumpType,
) => {
  if (data?.comment_uin) {
    const { query, pathname } = history.location || { query: {} };
    const params: any = {
      ...query,
      ...urlParams,
      comment_time: data?.comment_time,
    };
    const { search } = window.location;
    const redirectParam =
      jumpType === JumpType.Window && !params?.redirect_path
        ? `&redirect_path=${encodeURIComponent(
            stripBasePath(pathname + search),
          )}`
        : '';
    const resolvedUrl = `/opinion/Feeds/Feeds/Details/${data?.comment_uin}?${objToUrl(params)}${redirectParam}`;
    navigate(resolvedUrl, { jumpType });
  }
};

/**
 * 修改链接参数
 * @param obj     需要增量替换的参数
 * @param history
 */
export function setUrlInfo(obj: any = {}, history: any) {
  const { pathname, query } = history.location;
  const urlParams = objToUrl({
    ...query,
    ...obj,
  });
  const url = `${getBasePath() + pathname}?${urlParams}`;
  window.history.replaceState(null, '', url);
}

/**
 * 剥离部署前缀（/v2、/rebuildv2），返回 SPA 内部路由能识别的路径。
 *
 * 背景：/v2、/rebuildv2 是 nginx 层 rewrite 掉的前缀，SPA 内部路由表（如 /opinion）不含这些前缀。
 * 若把 window.location.pathname（可能带 /v2 前缀）直接拼进 history.push 或 redirect_path，
 * 会因匹配不到路由而 404。
 *
 * @param path 完整路径，如 /v2/opinion/Overview/KeyMetrics?gameid=xxx
 * @returns 剥离前缀后的内部路径，如 /opinion/Overview/KeyMetrics?gameid=xxx
 */
export function stripBasePath(path: string): string {
  if (!path) return path;

  const basePath = getBasePath();
  if (!basePath) return path;

  // 仅剥离处于开头的部署前缀（/v2、/rebuildv2），避免误伤路径中的同名片段
  if (path === basePath) return '/';
  if (path.startsWith(`${basePath}/`)) {
    return path.slice(basePath.length);
  }

  return path;
}

export function goToCompetitorKeyMetrics(
  history: any,
  filter: any,
  // plate:
  //   | 'Mentions & Sentiment'
  //   | 'Game Store'
  //   | 'Community'
  //   | 'PR'
  //   | 'Google Trends' = 'Mentions & Sentiment',
) {
  goStatsDetailAction({
    path: `/opinion/Overview/CompareKeyMetrics?gameid=${filter.edition_unified_id}`,
    params: filter,
    history,
  });
}

export const getOtherSystemUrl = (gameInfo: any) => {
  const { edition_unified_id, entity_type, combined_id } = gameInfo;

  if (combined_id) {
    return `/intelligence/topCharts/pcConsoleGames/gamesDetails/${combined_id}/overview`;
  }

  const urlMap: any = {
    pc: `/intelligence/topCharts/pcGames/gamesDetails/${edition_unified_id}/stats/pcu`,
    console: `/intelligence/topCharts/consoleGames/gamesDetails/${edition_unified_id}/stats/digitalRevenue`,
    mobile: `/intelligence/topCharts/mobileGames/gamesDetails/${edition_unified_id}/stats/activeusers`,
  };

  return urlMap[entity_type];
};

export function getSessionStorageId(gameInfo: any) {
  return `summaryFilter_${gameInfo?.edition_unified_id}`;
}

export const goToHotEvent = ({
  gameInfo,
  data,
  history,
  urlData,
}: {
  gameInfo: any;
  data: any;
  history: any;
  urlData?: any;
}) => {
  const storeKey = getSessionStorageId(gameInfo);
  sessionStorage.setItem(
    storeKey,
    JSON.stringify({
      languageValue: 'all',
      start_time: moment().format('YYYY-MM-DD'),
      end_time: moment().format('YYYY-MM-DD'),
      days: 1,
      filterType: ['nlp', 'config'],
      ...data,
    }),
  );

  const url = Object.keys(urlData || {}).length !== 0 ? objToUrl(urlData) : '';
  history.push(
    `/opinion/Summary/HotEvent?gameid=${gameInfo.edition_unified_id}&${url}`,
  );
};

/**
 *
 * @param filter 请求接口的参数
 * @param gameInfo 游戏数据
 * @param history
 * @param extraFilter 特殊参数
 */
export const clickTopicToFeedsWithTab = async ({
  filter,
  gameInfo,
  history,
  extraFilter = {},
}: {
  filter: any;
  gameInfo: any;
  history: any;
  extraFilter?: any;
}) => {
  const tab = await getFeedsStats({
    ...filter,
    ...gameInfo,
  }).then((data: any) => {
    let tab = 'socialMedia';
    const { social_total, comments_total } = data || {};
    if (social_total === 0 && comments_total > 0) {
      tab = 'gameStore';
    }
    return tab;
  });
  goStatsDetailAction({
    path: `/opinion/Feeds/Feeds?tab=${tab}`,
    params: {
      ...extraFilter,
      ...filter,
    },
    history,
  });
};

export function clickFeedsKeyword({
  keyword,
  filter,
  detailKey,
}: {
  keyword: {
    child: Array<{ keyword: string }>;
  };
  filter: any;
  detailKey: string;
  history: any;
}) {
  const children = keyword?.child?.map((info: any) => info.keyword);

  goStatsDetailAction({
    params: {
      filter: {
        ...filter,
        keyword: children,
        keyword_widely_match: true,
      },
      statsDetailType: detailKey,
    },
  });
}

/**
 * 判断是否为链接
 * @param url
 * @returns
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
}

export function getLinkDomain() {
  return location.origin;
}
